import { enBackendMessages } from "./backendMessages/en";

export const enTranslations = {
  common: {
    back: `Back`,
    cancel: `Cancel`,
    error: `Something went wrong`,
    form: {
      dateInput: {
        placeholders: {
          day: `DD`,
          month: `MM`,
          year: `YYYY`,
        },
        validation: {
          required: `Date is required`,
          incomplete: `Please complete the date`,
          invalid: `Please enter a valid date`,
          tooEarly: `Date is too early`,
          tooLate: `Date is too late`,
          tooYoung: `Must be at least {{minAge}} years old`,
          tooOld: `Must be no more than {{maxAge}} years old`,
          futureDate: `Date cannot be in the future`,
        },
      },
    },
    language: {
      english: `English`,
      french: `French`,
      german: `German`,
      select: `Select language`,
      spanish: `Spanish`,
      italian: `Italian`,
    },
    loading: `Loading...`,
    loadingPage: `Loading page...`,
    retry: `Try again`,
    save: `Save`,
    saving: `Saving...`,
    errors: {
      failedToGetOnboardingStatus: `Failed to get onboarding status`,
      failedToGetSubscriptionStatus: `Failed to get subscription status`,
      failedToCheckQuestionnaireProgress: `Failed to check questionnaire progress`,
      failedToFetchUserSettings: `Failed to fetch or apply user settings`,
    },
    backendMessages: enBackendMessages,
    maintenance: {
      title: `Scheduled maintenance`,
      description: `On {{dayOfWeek}}, {{date}}, between {{startTime}} and {{endTime}} maintenance will be carried out to improve the service. The app may be unavailable during this period.`,
      descriptionDateRange: `HARVN will be unavailable from {{startDate}} {{startTime}} until {{endDate}} {{endTime}}. We apologise for any inconvenience.`,
    },
    legal: {
      privacy: {
        changes: {
          p1: `The Company may update this Privacy Policy. We will notify you about significant changes in the way we treat personal data by sending a notice to the primary email address specified in your account or by posting such changes on the HARVN App(s). By continuing to use the Services after those changes become effective, you agree to be bound by the revised Privacy Policy.`,
          title: `8. Changes to our Privacy Policy`,
        },
        dataController: {
          address1: `Rue Charles-Bonnet 4`,
          address2: `1206 Geneva`,
          company: `HARVN Labs SA`,
          contactEmail: `contact@harvnlabs.com`,
          contactPrefix: `If, at any time, you have questions or concerns about our privacy practices, please contact us at`,
          label: `The data controller is:`,
          title: `1. Data Controller`,
        },
        introduction: {
          bold: {
            collect: `collect`,
            company: `Company`,
            consentPhrase: `I have read, understood and agree to the Privacy Policy`,
            fadp: `FADP`,
            gdpr: `GDPR`,
            privacyPolicy: `Privacy Policy`,
            services: `Services`,
          },
          p1: `This privacy policy (“Privacy Policy”) provides the policies and procedures of HARVN Labs SA (the “Company”) for collecting, using, storing and disclosing your Personal Data (as described below) through the services provided on the HARVN App(s) (collectively, our “Services”). This Privacy Policy governs the data protection of your Personal Data (as defined here below), regardless of what type of device or application you use to access our Services. By using our Service, you acknowledge that you have read and understood this Privacy Policy.`,
          pEmployer: `Where HARVN Pro is made available to you by your employer or another organisation, such organisation does not have access to the content of your individual conversations with HARVN Pro.`,
          p2: `The Company shall collect, process, store and share (collectively “collect”) your Personal Data (as defined below) in accordance with the Federal Act on Data Protection dated 25th September 2020 (“FADP”) and the General Data Protection Regulation (“GDPR”) whichever is applicable.`,
          p3: `The terms in this Privacy Policy – in particular the terms beginning with a capital letter – have the same meaning as the terms defined in the General Terms and Conditions.`,
          p4: `By clicking the “I have read, understood and agree to the Privacy Policy” button, you expressly consent to the processing any personal data, including sensitive personal data, in accordance with our Privacy Policy. You acknowledge that such data will be processed as described in this Privacy Policy. If you do not provide this consent, we ask that you refrain from using our services.`,
          title: `HARVN Labs SA - Privacy Policy`,
        },
        purposes: {
          analytics: {
            p1: `As explained above, we may also collect some data (ourselves or by using third party services) by using logging and cookies, such as IP addresses, which can sometimes be correlated with Personal Data. We use this data for the above purposes and to monitor and analyze the use of the Service, for the Service’s technical administration, to increase our Service’s functionality and user-friendliness, and to verify users have the authorization needed for the Service to process their requests.`,
            title: `3.2 Analytics`,
          },
          personalData: {
            item1: `provide the Services;`,
            item2: `contact or identify you;`,
            item3: `grant you access to the HARVN App(s) and our Services;`,
            item4: `comply with any requirement imposed by applicable law or by an order of a court or competent governmental or regulatory authority;`,
            item5: `provide and improve our Services and customer service;`,
            item6: `personalize and improve your experience;`,
            item7: `debug, monitor performance, ensure security, and understand how users interact with the HARVN App(s);`,
            item8: `manage enquiries;`,
            item9: `provide or offer software updates product announcements, newsletters or marketing communications, where you have provided your consent or where the law otherwise permits us to do so; and`,
            item10: `process voice input, convert speech to text, and generate AI-based responses at your request.`,
            title: `3.1 Personal data`,
            p1: `Personal Data may be collected to:`,
            noTraining: `HARVN does not use your conversations, prompts, outputs or uploaded documents to train, retrain or fine-tune artificial intelligence models.`,
          },
          title: `3. For which purpose do we use your personal data`,
        },
        retention: {
          p1: `We may retain and use your Personal Data as long as you use the Services and as long as necessary to comply with our contractual and legal obligations. Audio data submitted via voice features is processed temporarily for transcription purposes and is not stored after processing, except where technically necessary for the functioning of the service. Only transcribed text may be stored as part of your interaction history.`,
          p2: `Personal Data is retained only for as long as necessary for the purposes described in this Privacy Policy, unless longer retention is required or permitted by law. Thereafter, the data is deleted or anonymized.`,
          title: `5. Data retention and storage`,
        },
        rights: {
          p1: `You have the right to view, amend or delete the Personal Data that we hold about you, including sensitive data. If any of the Personal Data we hold on you is inaccurate or incomplete, you may directly amend your Personal Data in your User Profile.`,
          p2: `You may also request the deletion of the Personal Data or object to their processing or request to restrict their processing, except in the event that the request is unfounded, for instance in presence of a legal basis or an overriding interest of the Company.`,
          p3: `Where GDPR applies, you also have the right to data portability and the right to withdraw your consent at any time. You further have the right to lodge a complaint with the competent data protection supervisory authority.`,
          title: `7. Rights and requests`,
        },
        security: {
          linkLabel: `Microsoft Azure OpenAI Data, privacy and security page`,
          pAi1: `HARVN uses generative artificial intelligence. When interacting with HARVN, you are interacting with an AI system and not with a human being or professional adviser.`,
          pAi2: `HARVN is a decision-support and coaching tool and does not make automated decisions producing legal or similarly significant effects concerning you. You remain responsible for any decision or action taken on the basis of its outputs.`,
          pAi3: `AI-generated responses may be inaccurate, incomplete or otherwise contain errors.`,
          p1a: `We integrate artificial intelligence technology powered by Microsoft Azure OpenAI to support certain features of our application.`,
          p1bPrefix: `For further information please refer to the`,
          p2: `The Company applies technical and organizational measures to protect Personal Data from unauthorized access, loss, misuse, alteration, or disclosure, including access controls, encryption when needed, and contractual safeguards with service providers.`,
          p3: `We use Microsoft Azure OpenAI to process user chat messages, recent conversation history, conversation summaries, attached image content, and text extracted from attached files in order to generate responses, summarize conversations, and create embeddings for memory and search. In certain cases, limited contextual information such as the user's first name may be included for personalization purposes, and a coarse location hint may be included solely to improve safety-related responses.`,
          p4: `Where voice features are used, we rely on Microsoft Azure Speech services to process user voice recordings for speech-to-text transcription and to convert generated text into spoken audio for text-to-speech playback.`,
          p5: `If users upload documents to create a knowledge base within the HARVN App(s), those files may be processed by a document-conversion service to extract text, images, and structural information prior to indexing, in order to enable search and retrieval functionalities within the Services.`,
          title: `6. Security`,
        },
        sharing: {
          companyAccess: {
            p1: `HARVN applies a “zero trust” architecture designed to minimise access to user data. HARVN personnel do not routinely have access to the content of your conversations. Any exceptional access is limited to what is strictly necessary for technical troubleshooting, security, legal compliance or customer support and is subject to appropriate access controls and confidentiality obligations.`,
            p2: `Where HARVN Pro is made available through your employer or another organisation, such organisation does not have access to the content of your individual conversations with HARVN Pro.`,
            title: `4.2 Access by the Company`,
          },
          compliance: {
            description: `We may also disclose to third parties your Personal Data, or data collected from cookies and other logging data, when we have a good faith belief that disclosure is reasonably necessary to:`,
            item1: `comply with a law, regulation or compulsory legal request;`,
            item2: `protect the safety of any person from death or serious bodily injury;`,
            item3: `prevent fraud or abuse of the Company, its Services or its users; or`,
            item4: `to protect an overriding interest of the Company.`,
            title: `4.3 Compliance with laws and law enforcement requests; protection of the Company’s rights`,
          },
          title: `4. Information sharing and disclosure`,
          transfer: {
            p1: `Personal data may be transmitted to technical service providers acting as data processors on behalf of the Company, financial partners, supervisory authorities, or companies within the same group when necessary for the provision of services or the fulfillment of legal obligations in a country for which the level of data protection is deemed to be sufficient according to the Federal Data Protection and Information Commissioner. This may include the transmission of text inputs and, where applicable, audio data submitted through voice features to external AI processing providers (such as Microsoft Azure OpenAI) strictly for the purpose of providing the Services. If a transfer occurs to a country that does not offer a level of protection recognized as adequate, the Company implements appropriate safeguards, including the conclusion of Standard Contractual Clauses recognized by the competent data protection authorities.`,
            title: `4.4 Transfer of data`,
          },
          yourAccess: {
            p1: `Your Personal Data will be displayed in your User Profile, you can update it at any time.`,
            title: `4.1 Your access`,
          },
        },
        version: `Version 1.4 dated 01.09.2026.`,
        whatWeCollect: {
          localStorage: {
            p1: `We use local storage, device identifiers, and analytics tools to operate and improve the Services. Local storage helps save your preferences and login state. Device identifiers are used for app functionality, security, and analytics. We may also use third-party analytics to understand how users interact with the app and to enhance performance. You can manage or limit data collection through your device settings. Please note that disabling these features may limit some functionalities of the Services.`,
            title: `2.2 Local storage, device identifiers and analytics`,
          },
          logData: {
            p1: `When you use the Services, we may automatically collect certain log data to help us maintain and improve the Services. This may include the device’s Internet Protocol (“IP”) address, your device type, operating system, app version, browser type, identification numbers associated with your devices, your mobile carrier, crash reports, timestamps of usage and other technical information.`,
            title: `2.3 Log data`,
          },
          providePersonalData: {
            bold: {
              consentPhrase: `I expressly consent the processing of my sensitive personal data`,
              personalData: `Personal Data`,
            },
            p1: `When you register for, or access the Services, we collect personal data (“Personal Data”) necessary to create and manage a User Profile, such as your name and email address. If you choose to use voice input features, we may also process audio data that you voluntarily submit through the HARVN App(s). Such audio data is processed solely for the purpose of converting speech into text and generating AI-based responses.`,
            p2: `By voluntarily submitting personal, including sensitive, information through the HARVN App(s), you expressly consent to its processing by us by clicking the “I expressly consent the processing of my sensitive personal data” button, for the purposes set out in this Privacy Policy and to the extent necessary to provide the Services. If you do not wish to provide such information or do not consent to its processing, please refrain from submitting it through the App(s). We do not collect or process billing or payment information through the App(s). Any billing information, if applicable, is handled securely via external service providers, and is not stored within our systems.`,
            pVoice: `Where voice input is enabled, any audio recordings you actively submit are transmitted to our technical service providers for transcription and response generation. Audio data is not used for biometric identification and is not processed for the purpose of uniquely identifying you.`,
            p3: `It is your responsibility to refrain from entering personal sensitive data related to third parties into the HARVN App(s).`,
            p4: `You should not submit confidential business information, trade secrets, legally privileged information or other confidential information belonging to your employer or any third party unless authorised to do so.`,
            title: `2.1 Personal data you provide`,
            notice: `By clicking the “I consent to receiving newsletters and marketing emails” button, we will use your name and email address for this purpose. This consent is optional and can be withdrawn at any time.`,
          },
          title: `2. What personal data do we collect?`,
        },
        notice: `In the event of any discrepancy or inconsistency between different language versions of the Privacy Policy, the English version shall prevail and shall be deemed the authoritative version.`,
      },
      terms: {
        accessAndUse: {
          access: {
            bold: {
              userProfile: `User Profile`,
            },
            text1: `In order to access the services provided by the Company, Users must register on the HARVN App(s) by creating a user profile (the “User Profile”) and undertaking the validation process undertaken by the Company. Each User must create and use their User Profile. Account sharing or use on behalf of others is not permitted.`,
            text2: `The Company may determine, in its sole discretion, that it is necessary to obtain certain information about Users in order to comply with applicable law or regulation in connection with the use of the HARVN App(s). Users agree to provide such information promptly upon request and acknowledge that the Company may refuse to provide access to the HARVN App(s) until such requested information is provided. Should a User not be validated, they shall be blocked from accessing the HARVN App(s).`,
            text3: `If the User has chosen to subscribe to a paid version of the HARVN App(s). Failure to complete payment or renew your subscription will result in immediate suspension of access to those paid features, without prior notice. The Company may disable your account until payment is received and the subscription is reactivated.`,
            title: `Access`,
          },
          properUse: {
            intro: `Users must use the HARVN App(s) properly and in no event for the purpose of:`,
            list1: `engaging in illegal, harmful, or abusive behaviour,`,
            list2: `harassing others,`,
            list3: `submitting sensitive (for example, data related to health) personal data on other persons,`,
            list4: `submitting confidential business information, trade secrets, legally privileged information or other confidential information belonging to their employer or any third party, unless authorised to do so;`,
            list5: `infringing any applicable legal provisions or in violation with these Terms, additional applicable conditions and policies, or`,
            list6: `attempting to reverse engineer or misuse the HARVN App(s).`,
            outro: `Users who do not respect proper conduct while using the HARVN App(s) and its features may at any time be temporarily or definitively excluded from accessing the HARVN App(s) at the Company’s sole discretion and without indication of any grounds.`,
            title: `Proper use`,
          },
          restrictions: {
            contactEmail: `contact@harvnlabs.com`,
            contactPrefix: `Parents or legal guardians who believe that their child has an account are encouraged to contact us immediately at`,
            text1: `Users must be at least 16 years old and have the mental capacity to use the HARVN App(s); otherwise, the consent of a parent or legal guardian’s is required. By accessing or using the HARVN App(s), Users confirm that they meet these requirements.`,
            text3: `If we become aware that a User is under this age, we reserve the right to delete his/her account immediately.`,
            title: `Restrictions`,
          },
          title: `Access and use`,
        },
        billingAndPayment: {
          text1: `A monthly or a yearly subscription fee is charged for access to the HARVN App(s). Payments are processed via secure third-party providers. We do not retain full credit card details on our servers.`,
          text2: `The billing cycle begins on the date of the initial subscription and recurs on the same calendar day each month or year, depending on the selected billing frequency (monthly or annual). If the original billing date does not occur in a subsequent month (for monthly subscriptions), the charge will be processed on the last available day of that month. A free trial period of 30 days will be available, but is not mandatory. During the free trial period, usage of the HARVN App(s) will be limited. Invoices are issued and delivered by the third-party providers.`,
          text3: `In the event of a failed payment access to our services will be suspended. Users remain liable for all outstanding charges incurred prior to cancellation or suspension.`,
          text4: `Subscriptions may be cancelled at any time through the account settings interface. Cancellation becomes effective at the end of the current billing cycle.`,
          text5: `We reserve the right to adjust subscription fees. In such cases, users will be informed at least 30 days in advance by email and/or in-app notification. Continued use of the service after the new rate becomes effective constitutes acceptance of the revised fee.`,
          text6: `All charges are denominated in Swiss Francs (CHF) collected in Swiss Francs (CHF), Euros (EUR), US dollars (USD) or any other currencies depending on the User’s place of residence or location, whichever applicable. VAT is included unless explicitly stated otherwise. The applicable VAT rate is determined based on the subscriber’s place of residence or location, whichever is applicable, and is managed by our external payment providers or by HARVN Labs SA, who remit VAT directly to the relevant tax authorities. Users are responsible for any additional taxes, levies, or duties applicable in their relevant jurisdiction.`,
          title: `Billing and payment`,
        },
        governingLaw: {
          text1: `The provisions of these Terms shall be governed and construed in accordance with the laws of Switzerland.`,
          text2: `The courts of Geneva, Switzerland shall have exclusive jurisdiction over any claim arising from or related to the HARVN App(s).`,
          title: `Governing law and jurisdiction`,
        },
        informationAndSecurity: {
          text1: `The Company reserves the right, at any time and without prior notice, to modify any information provided to Users when updating or correcting errors and inaccuracies on the HARVN App(s).`,
          text2: `Users are solely responsible for maintaining the confidentiality and security of their username and password. Any activity conducted through the HARVN App(s) using valid login credentials will be deemed to have been performed by the respective User.`,
          text3: `The Company bears no responsibility for any loss, damage, or unauthorized access arising from the User’s failure to safeguard their credentials as per article 8 of these Terms. Users accept full liability for any consequences resulting from the misuse, theft, or compromise of their login information, regardless of whether such misuse was known or suspected.`,
          title: `Information and security`,
        },
        intellectualProperty: {
          text1: `Access to the HARVN App(s) and the information contained therein, does not in any way convey or transfer any right in or to the intellectual property rights of the Company or any other person in respect of data or information displayed or accessible from the HARVN App(s) (the “Material”) or in any of the trade marks, copyright, designs, patents, source code, browsing structure, software, domain names, know-how, confidential information, trade secrets or any other intellectual property rights which may vest in the Company or in the author, compiler, creator or licensor of the Material.`,
          text2Prefix: `The Company expressly reserves all rights in and to the Company name and logo, the domain names`,
          text2Suffix: `related sub domains and all other of the Company’s names, marks, taglines, or those relating to the HARVN App(s).`,
          text3: `Users acknowledge and agree that the Company owns and shall continue to own, exclusively, all legal property, right, title and interest in and to the HARVN App(s) and its content (including, but not limited to, software, designs, graphics, texts, information, pictures, video, sound, music, and other files, their selection and arrangement, trademarks, tradenames, service marks, branding features, business names, logos, slogans), including all intellectual property rights therein.`,
          text4: `Users are not required to provide the Company with any feedback or suggestions regarding the HARVN App(s). However, should a User provide the Company with comments or suggestions for the modification, correction, improvement or enhancement of thereof, then, such feedback shall be deemed non-confidential and the Company shall be entitled to use, reproduce, and incorporate it into the HARVN App(s) or other products or services without restriction, attribution, or compensation to the User.`,
          title: `Intellectual property`,
        },
        introduction: {
          bold: {
            company: `Company`,
            consentPhrase: `I have read, understood and agree to the General Terms and Conditions`,
            harvnApps: `HARVN App(s)`,
            parties: `Parties`,
            party: `Party`,
            terms: `Terms`,
            users: `User(s)`,
          },
          text1: `These General Terms and Conditions (the “Terms”) govern the access, use, features, services and content of the HARVN application(s) (“HARVN App(s)”).`,
          text2: `The HARVN App(s) is owned and operated by HARVN Labs SA (the “Company”), a company limited by shares, incorporated in Switzerland with registered seat at 2, rue Charles Bonnet, 1206 Geneva, registered under the IDE number: CHF-394.409.622.`,
          text3: `By accessing the HARVN App(s) Users will be required to confirm the following: “I have read, understood and agree to the General Terms and Conditions”, and to acknowledge the Privacy Policy. Users fully agree to be bound to these Terms and will fully comply herewith and agree to be a party to this binding contract. The use of and access to the HARVN App(s) will at all times be governed by the provisions of these Terms and by using and/or accessing the HARVN App(s) the User accepts fully all these Terms and agrees to be bound by and comply fully therewith. If the User does not wish to be bound by and comply with these Terms in full, he should not access and use the HARVN App(s).`,
          text4: `By using or accessing the HARVN App(s) in any manner, the User affirms, represents and warrants (a) that he has read, understood and agreed to be bound by all of the terms and conditions set forth in these Terms (including any additional terms, policies and agreements specified and/or referenced below or otherwise incorporated into these Terms as well as any subsequent modifications of these Terms), and (b) that his access and use of the HARVN App(s) is permitted by, and shall not violate, any applicable laws, including those in the User’s jurisdiction.`,
          text5: `If the User does not agree with these Terms, the User is prohibited from visiting, accessing or using the HARVN App(s).`,
          text6: `Users of the HARVN App(s) are specifically referred to below as “User(s)”.`,
          text7: `The Company and Users are individually referred to as a “Party” and collectively as the “Parties”.`,
          text8: `In the event of a discrepancy between general information provided on the HARVN App(s) and the Terms, the Terms prevail.`,
          text9: `The Company hereby grants the User a limited, revocable, non-exclusive, non-transferable, and non-sublicensable license, subject to these Terms, to install and use the relevant HARVN App(s).`,
          title: `Introduction`,
        },
        liability: {
          aiUse: {
            bold: {
              asIs: `“as is”`,
            },
            intro: `The User acknowledges and agrees that:`,
            linkLabel: `Microsoft Enterprise AI Services Code of Conduct`,
            list1: `AI-generated responses may be inaccurate, incomplete, or offensive;`,
            list2: `The User is solely responsible for any decisions made or actions taken based on AI output;`,
            list3: `The Company does not guarantee the accuracy, reliability, or suitability of AI-generated content for any specific purpose;`,
            list4: `AI content must not be relied upon as a substitute for professional advice, particularly in the fields of mental health, medicine, law, or finance.`,
            text1: `The HARVN App(s) uses artificial intelligence technology, including integration with Microsoft Azure OpenAI, to generate content. When interacting with the HARVN App(s), Users acknowledge that they are interacting with an artificial intelligence system and not with a human being or professional adviser.`,
            text2Prefix: `For further information please refer to the`,
            text3: `HARVN Labs SA reserves the right to modify, replace, or supplement the AI technologies used in its services at any time, including by integrating other large language models (LLMs), whether provided by other third parties or developed in-house.`,
            text4: `Such content is provided “as is”, without review, verification, or human oversight.`,
            text5: `The HARVN App(s) are decision-support and coaching tools and do not make automated decisions producing legal or similarly significant effects concerning Users.`,
            text6: `User conversations, prompts, outputs and uploaded documents are not used by the Company to train, retrain or fine-tune artificial intelligence models.`,
            title: `Use of artificial intelligence`,
          },
          disclaimer: {
            text1: `Except where explicitly provided in these Terms or required by law, the Company makes no representations or warranties, express or implied, regarding the availability, accuracy, reliability, security, or quality of the HARVN App(s), software, services, or features.`,
            text2: `The App(s) may contain errors, bugs, or harmful components; the Company does not guarantee that such issues will be corrected.`,
            text3: `Users are responsible for taking appropriate precautions, including implementing their own safeguards and using antivirus protection.`,
            title: `Disclaimer of warranties`,
          },
          indemnity: {
            intro: `Users agree to unconditionally and irrevocably indemnify and hold the Company harmless against any and all losses, damages, claims, liabilities, or expenses (including legal fees) arising from third-party claims due to:`,
            list1: `misuse or unauthorized use of the HARVN App(s), services, software, or features;`,
            list2: `infringement of any third-party intellectual property rights;`,
            list3: `violation of these Terms;`,
            list4: `any wrongful act or omission by the User in connection with their access to or use of the App(s).`,
            title: `Indemnity`,
          },
          limitation: {
            intro: `To the fullest extent permitted by Swiss law, the Company shall not be liable for any loss, damage, or claims arising from:`,
            list1: `the loss, misuse, or unauthorized access to usernames or passwords;`,
            list2: `any unavailability, interruption, or suspension of the HARVN App(s), services, software, features, or facilities;`,
            list3: `the suspension or termination of a User’s access to the HARVN App(s) or related services;`,
            list4: `the unavailability or failure of any feature or function;`,
            list5: `reliance on any AI-generated content.`,
            outro: `Except in cases of gross negligence or wilful misconduct, the Company shall not be liable for any direct, indirect, special, incidental, or consequential damages, whether arising in contract, tort, or otherwise.`,
            title: `Limitation of liability`,
          },
          risk: {
            a: {
              text1: `The HARVN App(s) are not designed to detect, diagnose, prevent, or treat any mental health condition. They must not be used as a substitute for therapy, crisis intervention, or psychiatric care.`,
              text2: `The Company expressly disclaims any liability for outcomes resulting from Users experiencing suicidal thoughts, self-harm, or other mental health crises.`,
              text3: `Users who are in distress, contemplating self-harm, or at risk of harming themselves or others are urged to immediately contact local emergency services, a suicide prevention hotline, or a qualified mental health provider.`,
              text4: `No part of the App(s) constitutes a therapeutic or crisis management service, and the Company does not monitor, track, or intervene in such situations.`,
              title: `(a) Mental health, suicide, and self-harm`,
            },
            b: {
              text1: `Users must not use the HARVN App(s) to plan, encourage, or support illegal, violent, abusive, or harmful acts toward others.`,
              text2: `The Company shall not be liable for any damage, injury, loss, or criminal consequence arising from the misuse of AI-generated content for malicious, deceptive, or harmful purposes.`,
              text3: `The User assumes full responsibility for any consequence of actions taken based on AI-generated output, including any unlawful or unethical acts.`,
              title: `(b) Malicious use and harm to others`,
            },
            c: {
              text1: `The HARVN App(s) provide general lifestyle and professional coaching as well as self-development suggestions that may not be applicable to all users or situations.`,
              text2: `No guarantee is made regarding the effectiveness, appropriateness, or outcome of any recommendations.`,
              text3: `Users are solely responsible for interpreting and applying the content and are advised to seek personal advice from licensed professionals before making important decisions.`,
              title: `(c) Misinterpretation or over-reliance on content`,
            },
            d: {
              list1: `Individuals with severe psychological, emotional, or cognitive disorders;`,
              list2: `Individuals in treatment for addiction, trauma, or diagnosed mental illness;`,
              list3: `Minors without the supervision of a legal guardian.`,
              text1: `The HARVN App(s) are not intended for use by:`,
              text2: `The Company disclaims all responsibility for risks arising from use by vulnerable individuals or those acting outside the intended scope of the HARVN App(s).`,
              title: `(d) Vulnerable or high-risk users`,
            },
            e: {
              text1: `The Company shall not be liable for any personal, financial, professional, legal, or health-related decision made based on suggestions or responses from the HARVN App(s).`,
              text2: `This includes, but is not limited to, decisions relating to relationships, employment, legal strategy, personal health routines, or financial planning.`,
              text3: `Users are expected to exercise independent judgment and consult relevant professionals as needed.`,
              title: `(e) Decision-making, safety, and risk-based actions`,
            },
            f: {
              text1: `By using the HARVN App(s), the User acknowledges and accepts the above risks and agrees not to hold the Company liable for any direct or indirect outcomes, damages, or losses, regardless of the nature of the claim or its basis.`,
              text2: `In addition to the above-mentioned risks, there are other risks associated with your use of the HARVN App(s), including those that cannot be anticipated. Such risks may further materialize as unanticipated variations or combinations of the above-mentioned risks.`,
              title: `(f) User acknowledgment`,
            },
            title: `Risk use disclaimer and waiver of liability`,
          },
          title: `Liability, indemnity and disclaimer`,
        },
        miscellaneous: {
          amendment: `Amendment. The Company may modify these Terms at any time by, at its sole discretion, for any reason posting a revised version on the HARVN App(s) without advance notice to the Users. The modified provisions will become immediately effective upon posting or the date indicated in the posting. It is the User’s responsibility to check the HARVN App(s) regularly for amendments. The continued use of the HARVN App(s) after any amendment becomes effective constitutes the User’s acceptance of the amendment.`,
          bold: {
            amendment: `Amendment.`,
            entireAgreement: `Entire Agreement.`,
            severability: `Severability.`,
          },
          entireAgreement: `Entire Agreement. These Terms and the materials incorporated herein by reference, constitute the entire agreement between the Parties and supersede all prior, concurrent or subsequent agreements and understandings, both written and oral, between the Parties with respect to the subject matter hereof, including, without limitation, any public or other statements or presentations made by the Company or its affiliate.`,
          severability: `Severability. If any provision of these Terms is determined by a court of competent jurisdiction to be invalid, inoperative or unenforceable for any reason, the provision shall be separable from the remaining terms, which shall continue to be valid and enforceable.`,
          title: `Miscellaneous`,
        },
        processing: {
          bold: {
            sensitivePersonalInformation: `Sensitive Personal Information`,
          },
          text1: `By using the HARVN App(s), the User agrees that the Company may collect, process, and use certain personal data as described in the Company’s privacy policies harvnlabs.com/privacy-policy. Some data may be processed by third-party service providers, including but not limited to Microsoft Azure OpenAI (for AI-generated content) and our secured third-party partner(s) for payments.`,
          text2: `Users agree not to upload or otherwise make available to the Company any Sensitive Personal data concerning third-parties in connection with their use of the HARVN App(s). “Sensitive Personal Information” means data including but not limited to information revealing racial or ethnic origin, political opinions, religious or philosophical beliefs, or trade-union membership, genetic data, biometric data that could uniquely identify natural persons, data concerning health or data concerning a natural person's sex life or sexual orientation.`,
          text3: `The Company may also, notwithstanding any other provision of these Terms, use aggregated or anonymised User data that does not identify individual Users for statistical purposes and to improve the functionality and performance of the HARVN App(s). Such data shall not include the content of User conversations, prompts, outputs or uploaded documents for the purpose of training, retraining or fine-tuning artificial intelligence models.`,
          text4: `Please refer to the Company’s Privacy Policy and Cookie Policy for information on the collecting and processing of personal data collected through the HARVN App(s).`,
          title: `Processing of personal data`,
        },
        purposeAndLimitation: {
          limitation: {
            text1: `The HARVN App(s) are not a substitute for professional mental health, medical, legal or financial advice. If you are experiencing emotional distress, mental health challenges, or a crisis, we strongly encourage you to seek help from a qualified professional such as a licensed therapist, psychologist, or medical provider. In case of an emergency, please contact local emergency services or a crisis hotline immediately.`,
            title: `Limitation`,
          },
          purpose: {
            text1: `The HARVN App(s) are life and professional coaching assistants powered by artificial intelligence, including integration with Microsoft Azure OpenAI. The HARVN App(s) provide coaching, guidance, and self-improvement prompts exclusively upon User request. These tools are designed to support personal development and goal setting.`,
            title: `Purpose`,
          },
          title: `Purpose and limitation of the HARVN App(s)`,
        },
        representations: {
          intro: `By accessing the HARVN App(s), the User represent and warrant that:`,
          list1: `they are at least 16 years old and have the mental capacity to use the HARVN App(s); if not, the consent of a parent or legal guardian’s is required;`,
          list2: `they have read and understood these Terms;`,
          list3: `they will use the HARVN App(s) in accordance with these Terms and all applicable laws and regulations;`,
          list4: `they understand that the HARVN App(s) provide general guidance based on artificial intelligence, and does not constitute professional advice (medical, psychological, legal, financial, or otherwise); and`,
          list5: `they acknowledge that any actions or decisions taken based on the HARVN App(s) content are made at their own risk and discretion.`,
          text1: `We make no warranties regarding the accuracy, reliability, or availability of the HARVN App(s) or any content generated by the AI system. All information provided on the HARVN App(s) is provided “as is” without any warranty, whether express or implied and may occasionally be inaccurate, incomplete, or inappropriate. To the fullest extent permitted by law, all warranties—express or implied—are disclaimed.`,
          title: `Representations and warranties`,
        },
        version: `Last update: 01 September 2026.`,
        viruses: {
          text1: `Users must not misuse the HARVN App(s) by knowingly introducing or sending viruses, trojans, worms, logic bombs or other material that is malicious or technologically harmful. Users must not attempt to gain unauthorised access to the HARVN App(s) (which includes the server, cloud or any related data based). Users must not attack the HARVN App(s) or concerned blockchain by any means, in particular via a denial-of-service attack or a distributed denial-of-service attack.`,
          text2: `By breaching this clause, Users may be acting contrary to Swiss law. The Company will report any such breach to the relevant law enforcement authorities and will co-operate with those authorities by disclosing the User’s identity to them. In the event of such a breach, Users’ right to use the HARVN App(s) will cease immediately. To the maximum extent permissible by law, the Company will not be liable for any loss or damage caused by a distributed denial-of-service attack, viruses or other technologically harmful material that may infect Users’ telephone, tablet or computer equipment, programs, data or other proprietary material due to their use of the HARVN App(s).`,
          title: `Viruses, hacking and other offences`,
        },
        notice: `In the event of any discrepancy or inconsistency between different language versions of these General Terms and Conditions, the English version shall prevail and shall be deemed the authoritative version.`,
      },
    },
  },
  features: {
    auth: {
      login: {
        createAccount: `Create account`,
        description: `24/7 confidential support for clarity and growth.`,
        email: `Email`,
        errors: {
          accountLocked: `Account Temporarily Locked`,
          accountLockedDescription: `Your account is locked due to multiple failed login attempts. Please try again in {minutes} minute(s) or contact support for assistance.`,
          contactSupport: `Contact Support`,
          generalError: `Error while signing in`,
          invalidCredentials: `Incorrect email or password. Please try again.`,
          invalidEmail: `Please enter a valid email address.`,
          invalidPassword: `Password is incorrect.`,
          supportEmail: `support@harvn.com`,
          tryAgain: `Try Again`,
          warningDescription: `{errorMessage}. {attemptsRemaining} attempt(s) remaining before account lock.`,
          warningTitle: `Login Failed - Warning`,
          fallbackError: `Invalid email or password`,
          entraUnauthorized: `Sign-in failed. Please try again or use another sign-in option.`,
        },
        forgotPassword: `Forgot your password?`,
        login: `Log in`,
        or: `OR`,
        password: `Password`,
        quote: {
          text: `No one is watching you here. You start seeing yourself.`,
        },
        rememberMe: `Remember me`,
        welcome: `Welcome to HARVN.`,
        socialGoogle: `Continue with Google`,
        socialApple: `Continue with Apple`,
        socialMicrosoft: `Continue with Microsoft`,
        socialUnavailable: `Social sign-in is not available. Use email or try again later.`,
        socialGoogleSessionRequired: `Please sign in to your Google account in this browser and try again.`,
        socialSigningIn: `Signing in…`,
      },
      socialLinking: {
        title: `Link your account`,
        description: `This social sign-in matches an existing HARVN account. Sign in with the same provider again to link it to your profile.`,
        passwordStepDescription: `Enter the email and password for your existing HARVN account. We will then ask your browser to confirm with Google or Apple once so we can finish linking—this is a one-time step for this account.`,
        linkRetryHint: `Linking could not be completed. Try again with the same provider, or use Back to log in if you need a different account.`,
        resumeLinkingHint: `You were linking a social account. Sign in with your email and password to continue.`,
        emailHint: `{{email}}`,
        continueGoogle: `Continue with Google`,
        continueApple: `Continue with Apple`,
        backToLogin: `Back to log in`,
      },
      accountCompletion: {
        title: `Complete your account`,
        description: `To finish setting up your HARVN account, please confirm your name, date of birth, and accept the required terms.`,
        firstName: `First name`,
        lastName: `Last name`,
        dateOfBirth: `Date of Birth`,
        termsLabel: `I have read, understood and agree to the General Terms and Conditions.`,
        privacyLabel: `I have read, understood and agree to the Privacy Policy and consent to the processing of my sensitive personal data.`,
        submit: `Complete account`,
        submitting: `Completing your account…`,
        genericError: `We couldn't complete your account. Please review the form and try again.`,
      },
      acceptInvite: {
        title: `Accept your invitation`,
        description: `Set your password to activate your HARVN account.`,
        newPassword: `New password`,
        confirmPassword: `Confirm password`,
        submit: `Accept invitation`,
        submitting: `Accepting invitation…`,
        goToLogin: `Go to login`,
        successTitle: `Invitation accepted`,
        successDescription: `Password has been set successfully. You can now log in to HARVN.`,
        invalidTitle: `Invalid invitation`,
        invalidDescription: `Invitation link is invalid or has expired. Please contact your administrator for a new invitation.`,
        passwordRequired: `Password is required`,
        confirmationRequired: `Please confirm your password`,
        passwordLength: `Password must be 8–40 characters`,
        passwordsMismatch: `Passwords do not match`,
        passwordValidation: `Please check your password and try again.`,
        tooManyAttempts: `Too many attempts. Please try again later.`,
        serviceUnavailable: `We couldn't accept your invitation right now. Please try again later.`,
        genericError: `We couldn't accept your invitation. Please try again.`,
      },
      accessDenied: {
        title: `Access is currently unavailable`,
        description: `Please contact your organization administrator or support.`,
        supportHint: `Your organization administrator can help restore access.`,
        signOut: `Sign out`,
      },
      orgDisclaimer: {
        title: `Disclaimer – Privacy and Liability`,
        accept: `I agree`,
        localeSwitch: `Disclaimer language`,
        localeFr: `FR`,
        localeEn: `EN`,
        acceptErrorTitle: `Acceptance failed`,
        acceptErrorDescription: `We could not record your acceptance. Please try again.`,
      },
      logout: {
        success: `Logged Out`,
        successDescription: `You have been successfully logged out`,
        error: `Logout Failed`,
        errorDescription: `Failed to log out properly. Your session has been cleared locally, but server cleanup may be incomplete. Please try logging in again if you experience any issues.`,
      },
      session: {
        expiredTitle: `Session Expired`,
        expiredDescription: `Your session has expired. Please log in again.`,
        notLoggedInTitle: `Not Logged In`,
        notLoggedInDescription: `You must be logged in to access this page.`,
      },
      passwordReset: {
        description: `Enter the email address associated with you account, and we’ll email you a link to reset your password.`,
        email: `Email`,
        sendLink: `Send reset link`,
        successDescription: `Password reset successful`,
        title: `Reset password`,
        messages: {
          success: `Password recovery email sent`,
          successDescription: `We’ve sent a password reset link to your email. Please check your inbox.`,
          errorTitle: `Reset Failed`,
          error: `We couldn't send the reset link. Please verify your email and try again.`,
        },
      },
      paymentSuccess: {
        continue: `Continue`,
        description: `You're all set! Your payment was successful, and your {{planType}} HARVN subscription is now active. You will have full access to all benefits, features and tools.\n\nAny changes can be made via your Settings page.\n\nThank you for being a part of our community, HARVN is here to provide guidance and coaching for a better you.`,
        boldText: `{{planType}} HARVN subscription is now active`,
        title: `Payment Successful!\nYou now have unlimited access to HARVN.`,
        toast: {
          description: `Your subscription is now active`,
          title: `Subscription settings have been updated in your Settings`,
        },
        freemium: {
          title: `You can now start using Harvn.`,
          description: `You're all set! Your Free trial HARVN subscription is now active. You will have limited access to all benefits, features and tools.\n\nAny changes to your subscription plan can be made on the Settings page.`,
        },
      },
      resetPassword: {
        confirmPassword: `Confirm password`,
        description: `Enter your new password below.`,
        error: `Failed to reset password. Please try again.`,
        errorTitle: `Reset Failed`,
        expiredTokenDescription: `This reset link has expired. Please request a new one.`,
        expiredTokenTitle: `Reset Link Expired`,
        invalidTokenDescription: `This reset link is invalid or has been used. Please request a new one.`,
        invalidTokenTitle: `Invalid Reset Link`,
        loginMessage: `Password reset complete. Please log in with your new password.`,
        newPassword: `New password`,
        resetPassword: `Reset password`,
        success: `Password reset successful!`,
        successDescription: `Your password has been reset successfully.`,
        title: `Create new password`,
      },
      signup: {
        alreadyHaveAccountText: `Already have an account?`,
        createAccount: `Create account`,
        description: `24/7 confidential support for clarity and growth.`,
        email: `Email`,
        dateOfBirth: `Date of Birth`,
        loginLink: `Log in`,
        firstName: `First name`,
        lastName: `Last name`,
        or: `OR`,
        password: `Password`,
        confirmPassword: `Confirm password`,
        passwordHelper: `At least 8 characters, including at least one uppercase letter, one number, and one symbol.`,
        passwordsMismatch: `Passwords do not match`,
        privacyAgreementStart: `I have read, understood and agree to the`,
        privacyAgreementLink: `Privacy Policy.`,
        privacyAgreementEnd: `I expressly consent to the processing of my sensitive personal data.`,
        success: `Account created successfully!`,
        successDescription: `Your account has been created successfully.`,
        error: `Failed to create account`,
        errorDescription: `Failed to create account`,
        signupFailed: `Signup failed`,
        quote: {
          text: `Your reflections remain yours, sealed and encrypted.`,
        },
        rememberMe: `Remember me`,
        termsAgreement: `I have read, understood and agree to the`,
        termsAgreementLink: `General Terms and Conditions.`,
        welcome: `Let noise fade; \nhear yourself again.`,
        validation: {
          dateOfBirthRequired: `Date of birth is required`,
          dateOfBirthFormat: `Date must be in YYYY-MM-DD format`,
          dateOfBirthYearDigits: `Year must be exactly 4 digits`,
          dateOfBirthMinAge: `You must be at least 16 years old`,
          dateOfBirthMaxAge: `Age must be 120 years or younger`,
          dateOfBirthFuture: `Date cannot be in the future`,
          emailRequired: `Email is required`,
          emailInvalid: `Invalid email address`,
          emailLocalPartMax: `Email address before @ must be at most 255 characters`,
          passwordRequired: `Password is required`,
          passwordMin: `Password must be at least 8 characters`,
          passwordMax: `Password must be at most 40 characters`,
          passwordComplex: `Password must contain at least one uppercase letter, one lowercase letter, and one number or special character`,
          confirmPasswordRequired: `Please confirm your password`,
          passwordsMismatch: `Passwords do not match`,
          firstNameMin: `First name must be at least 2 characters`,
          firstNameMax: `First name must be at most 255 characters`,
          lastNameMin: `Last name must be at least 2 characters`,
          lastNameMax: `Last name must be at most 255 characters`,
          termsRequired: `You must agree to the General Terms and Conditions`,
          privacyRequired: `You must agree to the Privacy Policy`,
        },
      },
      subscription: {
        continue: `Continue`,
        description: `Choose your plan to continue with HARVN.`,
        expired: {
          close: `Close`,
          description1: `You’ve reached the limit of your Free access.`,
          description2: `Your allowance has been fully used and your account is now paused. To continue your journey with HARNV, upgrade your plan.`,
          description3: `Once upgraded your access will be restored, you’ll then have unlimited access to continue using HARVN.`,
          description4: `Once upgraded your access restored, you will have unlimited access and continue using HARVN.`,
          title: `Trial access has expired`,
          upgradeSubscription: `Upgrade Subscription`,
        },
        lifePlan: `Life Plan`,
        monthly: `Monthly`,
        proPlan: `Pro Plan`,
        title: `Complete your subscription`,
        validation: {
          continueFreemium: `Continue Trial`,
          description1: `Your access to HARVN Trial is almost up. You have {{tokenRemaining}}.`,
          description2: `To keep going without restrictions and interruptions, consider upgrading your plan within the next 7 days to avoid losing access.`,
          description3: `Your trial access is available until {{date}}, after which your account will be paused.`,
          title: `Trial access is almost up`,
          upgradeSubscription: `Upgrade Subscription`,
        },
        yearly: `Yearly`,
      },
      subscriptionExpired: {
        contactSupport: `Contact support`,
        description: `Your subscription has expired. Renew to continue.`,
        renew: `Renew subscription`,
        title: `Subscription expired`,
      },
      termsOfService: {
        back: `Back`,
        title: `General Terms and Conditions`,
      },
      updatePassword: {
        confirmPassword: `Confirm password`,
        description: `Create a new password for your account.`,
        error: `Failed to update password. Please try again.`,
        newPassword: `New password`,
        saveChanges: `Save changes`,
        success: `Password updated successfully!`,
        successDescription: `Your password has been updated successfully.`,
        title: `Update your password`,
      },
      verification: {
        sent: {
          continue: `Continue`,
          description1: `A confirmation email was sent to`,
          description2: `Click the link to verify your email and complete your registration.`,
          resendEmailText: `Didn't receive email? Check your spam folder or`,
          resendEmailAction: `send again`,
          title: `We've send a verification link to your email.`,
        },
        success: {
          continue: `Continue`,
          description: `Your HARVN account has been verified.`,
          title: `Success. You're all set.`,
        },
        resend: {
          title: `Verification Email Sent`,
          description: `Please check your email for verification instructions.`,
          errorTitle: `Failed to Send Email`,
          errorDescription: `Failed to resend verification email`,
        },
        verify: {
          successTitle: `Email Verified`,
          successDescription: `Your email has been successfully verified.`,
          errorTitle: `Verification Failed`,
        },
      },
      freemiumSuccess: {
        continue: `Continue`,
        description: `You're all set! Your 30 day Free Trial is now active.`,
        settingsInfo: `Any changes to your plan can be made on the Settings page.`,
        title: `You can now start using HARVN.`,
        successToast: `Plan settings have been updated in your Profile`,
      },
      trialExpired: {
        title: `Free access has expired`,
        description: `You’ve reached the limit of your Free trial. You’ve used the full allowance, your account is now paused.`,
        upgradeInfo: `To continue your journey with HARNV, `,
        upgradeBold: `upgrade your plan.`,
        accessInfo: `Once upgraded you’ll then have unlimited access to continue using HARVN.`,
        upgradePlan: `Upgrade Plan`,
        logout: `Log out`,
      },
    },
    chat: {
      main: {
        descriptionStart: `I'm here to offer clarity, guidance and reflection, to help you navigate whatever's on your mind.`,
        descriptionBold: `This is your space`,
        descriptionEnd: `and I'm here for your self discovery.`,
        filesAttached: `📎 Files attached`,
        footerText: `HARVN only makes suggestions.`,
        piiPersonWarning: `This looks like a person's name. Please don't enter names or personal data; describe the role instead.`,
        answerForm: "Answer form",
        answerFormHeader: "ANSWER FORM",
        answerFormFull: "Full analysis",
        answerFormFullHint: "The nine parts, for this message",
        answerFormDirect: "Direct answer",
        answerFormDirectHint: "Answers your question as asked, for this message",
        answerFormRemove: "Remove answer form",
        answerFormAdded: "{{form}} answer form added.",
        answerFormReplaced: "Answer form changed to {{form}}.",
        answerFormRemoved: "Answer form removed.",
        greeting: `Hello {{name}}, I'm HARVN.`,
        pro: {
          greeting: `Harvn Pro for {{organizationName}}`,
          description: `Harvn helps managers turn the Edmond Way into clear, high-quality decisions, exactly when it matters most.`,
        },
        placeholder: `What’s on your mind at the moment?`,
        placeholderReturning: `Message`,
        removeFile: `Remove file`,
        selectFiles: `Select files to upload`,
        uploadingFiles: `Uploading files...`,
        copy: `Copy`,
        copied: `Copied`,
        copyTooltip: `Copy message`,
        copyError: `Failed to copy message`,
      },
      message: {
        failedToSend: `Failed to send`,
        retry: `Retry`,
      },
      voice: {
        recordButton: `Record voice message`,
        recording: `Recording...`,
        processing: `Processing...`,
        permissionDenied: `Microphone permission denied`,
        unsupportedBrowser: `Voice recording not supported in this browser`,
        recordingError: `Failed to record audio`,
        transcriptionError: `Failed to transcribe audio`,
        speakerButton: `Play message`,
        playing: `Playing...`,
        loading: `Loading audio...`,
        playbackError: `Failed to play audio`,
        stopRecording: `Stop recording`,
        maxDurationReached: `Maximum recording duration reached`,
        disabledDuringRecording: `Audio playback disabled during recording`,
        englishOnlyNotice: `Only the English language is supported.`,
      },
      sidebar: {
        chatDeleted: `Chat removed`,
        chatDeletedDesc: `Chat has been successfully removed.`,
        chatHistory: `CHAT HISTORY`,
        chatRenamed: `Chat renamed`,
        chatRenamedDesc: `Chat has been successfully renamed.`,
        deleteError: `Failed to remove chat. Please try again.`,
        enterNewName: `Enter new chat name`,
        forMe: `FOR ME`,
        helpHub: `Help hub`,
        learningSupport: `LEARNING & SUPPORT`,
        limitedAccess: `You’re almost at the end of your free trial`,
        freemiumAccessTitle: `You're currently using the {{days}} day Free Trial`,
        freemiumAccessDescription: `Upgrade your plan to ensure uninterrupted access.`,
        daysRemaining: `days remaining`,
        logOut: `Log out`,
        myProfile: `My Profile`,
        myKnowledgeFiles: `My knowledge files`,
        newChat: `New chat`,
        removeChat: `Remove Chat`,
        renameChat: `Rename Chat`,
        renameChatTitle: `Rename Chat`,
        renameError: `Failed to rename chat. Please try again.`,
        saveChanges: `Save changes`,
        settings: `Settings`,
        upgradePlan: `Upgrade plan`,
        upgradeText: `Upgrade your plan to get unlimited access.`,
        questionnaire: {
          title: `Do you have time to add to your questionnaire?`,
          description: `I'm missing some context, feel free to include any additional details.`,
          finishButton: `Finish questionnaire →`,
        },
        sessionTime: {
          justNow: `Just now`,
          minuteAgo: `1 minute ago`,
          minutesAgo: `{{count}} minutes ago`,
          hoursAgo: `{{count}} hour ago`,
          hoursAgo_other: `{{count}} hours ago`,
          dayAgo: `1 day ago`,
          daysAgo: `{{count}} days ago`,
          weekAgo: `1 week ago`,
          weeksAgo: `{{count}} weeks ago`,
          monthAgo: `1 month ago`,
          monthsAgo: `{{count}} months ago`,
        },
      },
    },
    helpHub: {
      faq: {
        description: `We've compiled the most important information to help you get the most out of your experience. Can't find what you're looking for?`,
        title: `Frequently asked questions`,
        contactUs: `Contact us`,
      },
      sections: {
        billing: {
          questions: {
            question1: {
              answer: `We offer a 30-day free trial to all new users, without requesting a credit card. This trial gives you full access to all HARVN's features so you can test if it's right for you. You can choose a subscription plan (monthly or yearly) anytime during the 30 days of the trial. Doing so will automatically end the free trial and trigger the first payment. Choosing a plan before the trial period comes to an end, makes sure that you can continue your HARVN experience going without interruption. We'll send you a reminder 2 days before the end of the trial period to ensure that if you find value in using HARVN you can continue enjoying it for as long as you wish, paying for the subscription plan that best suits you. At the end of the 30-day free trial, accounts that have not chosen a subscription plan will be automatically suspended.`,
              question: `How does the free trial work, and what happens after it ends?`,
            },
            question2: {
              answer: `The features and access are the same for both plans – you get everything HARVN offers on either plan. The difference lies in billing and savings. A Monthly Plan is paid month-to-month, offering flexibility to cancel anytime. A Yearly Plan is paid upfront for the full year, usually at a discounted rate (you save in total cost compared to 12 months of the monthly plan). Choosing annual means you're committing to a year of personal growth (and getting a better deal financially), whereas monthly lets you go one step at a time.`,
              question: `What's the difference between the monthly and yearly plans?`,
            },
            question3: {
              answer: `Updating your payment method is easy and hassle-free. Simply go to your Account Settings (profile billing section) when logged in to HARVN. There, you can add or replace your credit/debit card or switch payment providers. If you subscribed via a mobile app store (Apple App Store or Google Play), you might need to update your payment information through your Apple/Google account settings. We're here to help – if you run into any issues changing your payment details, just reach out to our support team and we'll guide you through it. Rest assured, any changes you make will apply securely to your next billing cycle.`,
              question: `How can I change my payment method?`,
            },
            question4: {
              answer: `You can cancel your subscription at any time – no questions asked. If you cancel, you will continue to have full access to HARVN for the remainder of the period you've already paid for (until the end of your current billing cycle). We do not provide refunds or partial credits for unused time after cancellation, so your subscription will simply stop at the next renewal and you won't be charged going forward. (For example, if you cancel a monthly plan mid-month, you can still use HARVN until that month ends, but it won't renew.) Our aim is to make cancellation straightforward and stress-free – you're always in control of your subscription.`,
              question: `What is your cancellation policy?`,
            },
            question5: {
              answer: `If you ever decide to leave, you can cancel your HARVN account directly from the app or website in your account settings. Deleting your account will permanently erase all your personal data from our servers as a privacy safeguard. We take data deletion seriously – once you confirm deletion, we will keep your data safe for 90 days (should you change your mind) and after that proceed to a permanent deletion. There is no way to recover your account or conversations after deletion. Your trust is our priority, so we've made the deletion process transparent and in your control – and we never keep backups of your private entries after you choose to delete them.`,
              question: `How do I delete my account, and can I recover it later?`,
            },
          },
          title: `Billing`,
        },
        general: {
          questions: {
            question1: {
              answer: `Yes – absolutely. We take your privacy very seriously. Your conversations remain completely confidential and are protected by robust data encryption and GDPR-compliant practices. All data is stored on secure servers in Switzerland (with AES-256 encryption) to ensure the highest level of data privacy and security. No one except you can access your personal reflections, and we never share your data with third parties. This privacy-by-design approach is core to HARVN's values of trust and safety.`,
              question: `Is my data secure and private?`,
            },
            question2: {
              answer: `No, HARVN isn't a therapist – it's an AI-powered self-reflection and coaching tool. HARVN is designed to help you think through your thoughts and feelings, but it does not replace professional therapy. It guides you back to clear thinking at your own pace, acting as a supportive personal space and coach. While HARVN can be a great complement to your mental well-being routine, it isn't a licensed medical or therapeutic service. If you're facing serious mental health issues, we encourage you to seek help from a qualified professional. HARVN is here to support your personal growth and clarity, not to provide medical advice or diagnosis.`,
              question: `Is HARVN a form of therapy or counseling?`,
            },
            question3: {
              answer: `HARVN is not just another chatbot – it's built uniquely for personal growth using existential and systemic frameworks. Unlike generic AI apps, HARVN remembers your past conversations, emotions, and goals to provide tailored guidance. The AI adapts to you over time, evolving the dialogue based on your words and exchanges. It also uses guided, sincere prompts (no mindless small talk or flattery) to encourage true self-inquiry. In short, HARVN acts like an intelligent mirror for your inner world: always available 24/7, deeply personalized to you, and focused on helping you gain insight. This dedicated focus on structured self-reflection sets HARVN apart from typical chatbots or wellness apps.`,
              question: `What makes HARVN different from other AI or mental health apps?`,
            },
            question4: {
              answer: `Yes! Your HARVN account works across multiple devices. You can log into HARVN on your smartphone, tablet, and even via a web app, and all your progress and conversations will stay securely synced. Whether you're at home or on the go, HARVN is available 24/7 on the device of your choice, and your private data remains protected across all platforms. One subscription covers your account on all your devices, so you can reflect whenever and wherever you need to.`,
              question: `Can I use HARVN on multiple devices?`,
            },
          },
          title: `General`,
        },
      },
      title: `Help hub`,
    },
    knowledgeFiles: {
      title: `My knowledge files`,
      infoBanner: {
        title: `Please keep in mind`,
        description: `Uploaded files may be referenced within your conversations and context. If a file is deleted, previous references will remain, but the file will no longer be used or accessed moving forward.`,
      },
      fileHub: {
        title: `Your file hub`,
        subtitle: `Upload and delete your Knowledge files`,
      },
      upload: {
        buttonText: `Upload new document`,
        title: `Upload Files`,
        dragDrop: `Drag or upload a file`,
        allowedTypes: `Supported files: PPT, Excel, PDF document, .txt, .rtf`,
        maxSize: `Max size: {{maxSize}}MB per file`,
        maxSizeShort: `Max. {{maxSize}}mb`,
        maxSizeDescription: `Maximum upload per file 35mb`,
        uploading: `Uploading...`,
        processing: `Processing...`,
        uploadSuccess: `Your file has been uploaded successfully. Its content is now available for use in future conversations.`,
        uploadError: `This file type isn't supported`,
        modalTitle: `Upload Knowledge Files`,
        addNewDocument: `Add a new document`,
        modalInfo: `Don't close this modal during upload`,
        warningMessage: `Please don't leave this page while upload is in process.`,
        selectFiles: `Select file`,
        filesUploaded: `Files uploaded: {{current}}/{{total}}`,
        filesReadyForUpload: `Files ready for upload {{count}}/{{count}}`,
        statusUploading: `Uploading...`,
        statusPending: `Pending...`,
        statusCompleted: `Completed`,
        startUpload: `Start upload`,
        done: `Done`,
        retry: `Retry`,
      },
      fileList: {
        fileName: `File Name`,
        fileType: `File Type`,
        dateAdded: `File Added`,
        status: `Status`,
        sortByFileName: `Sort by file name`,
        sortByDateAdded: `Sort by date added`,
        sortByStatus: `Sort by status`,
        statusLabels: {
          idle: `Idle`,
          pending: `Pending`,
          uploading: `Uploading`,
          uploaded: `Uploaded`,
          processing: `Processing`,
          readyForUse: `Ready to use`,
          processingFailed: `Processing failed`,
          failed: `Failed`,
          deleted: `Deleted`,
          unknown: `Status unavailable`,
        },
        actions: `Actions`,
        noFiles: `No knowledge files yet`,
        noFilesDescription: `Upload files to enhance your HARVN experience`,
        delete: `Delete`,
        remove: `Remove`,
        deleteSuccess: `The file has been removed`,
        deleteError: `Failed to delete file`,
        deleteModal: {
          title: `Are you sure you want to delete this file?`,
          description: `Once deleted, this file will no longer be used or referenced in future conversations. Previous references will remain in context.`,
          confirmButton: `I understand, delete file`,
        },
      },
      status: {
        uploading: `Uploading`,
        processing: `Processing`,
        completed: `Ready`,
        error: `Error`,
      },
      errors: {
        unsupportedType: `Unsupported file type. Please upload PDF, Word, Excel, or PowerPoint files.`,
        fileTooLarge: `File size exceeds {{maxSize}}MB limit`,
        uploadFailed: `Failed to upload file`,
        fetchFailed: `Failed to load files`,
      },
      pagination: {
        previous: `Previous`,
        next: `Next`,
        pageOf: `Page {{current}} of {{total}}`,
      },
      accessDenied: {
        title: `Pro Feature`,
        description: `Knowledge Files is available for Pro users only`,
      },
      organizationAccessDenied: {
        title: `Personal knowledge files unavailable`,
        description: `Your organisation does not allow personal Knowledge Files.`,
      },
    },
    onboarding: {
      shared: {
        onboardingCompleteSyncFailed: {
          title: `We could not update your account`,
          description: `Your questionnaire was saved, but confirming your profile on our servers failed. Try signing out and back in, or contact support if this keeps happening.`,
        },
      },
      introduction: {
        continue: `Continue`,
        description: `An intelligent mirror for your inner world.`,
        heroLabel: `Introducing HARVN`,
        title: `What if you could see your thoughts clearly?`,
      },
      introductionTwo: {
        continue: `Continue`,
        description: `An intelligent mirror for your inner world.`,
        heroLabel: `You + HARVN`,
        titleDesktop: `Your mind comes into focus here.`,
      },
      questionnaireInvitation: {
        description1: `HARVN listens to your words. Answer 8 quick questions — private, encrypted, and kept only for you.`,
        description2: `Your answers shape your guidance and set the tone for your practice.`,
        description3: `Take the time you need; you can return anytime.`,
        disclaimer: `We'll send a gentle reminder if you choose to finish later.`,
        quote: {
          text: `Honesty unlocks clarity`,
        },
        remindButton: `Remind me later`,
        startButton: `Start questions`,
        subtitle: `Personal reflections`,
        title: `Let's start with you.`,
      },
      questionnaireStepper: {
        completeQuestionnaire: `Submit questionnaire`,
        nextQuestion: `Next question`,
        previousQuestion: `Previous question`,
        someFoodForThought: `Some food for thought`,
        helpText: `Maximum characters: 1900`,
        loading: `Loading your questionnaire...`,
        saving: `Saving...`,
        saved: `Previously answered`,
        errorMessage: `Failed to save your answer. Please try again.`,
        errorLoadingMessage: `Failed to load existing questions. You can still continue with the questionnaire.`,
        characterCount: `{{current}}/{{max}} characters`,
        characterLimitExceeded: `Your answer exceeds the maximum character limit of {{max}}. Please shorten your response.`,
        characterLimitWarning: `You're approaching the character limit ({{remaining}} characters remaining).`,
        dismiss: `Dismiss`,
        answerSubmittedMessage: `You have already answered this question.`,
        placeholder: `Share your thoughts here...`,
        questions: {
          step1: {
            description: `This first step invites you to describe yourself in the present, as if you were getting to know yourself. Let the words come to you, what defines who you are today and what’s important to you?`,
            questions: `Who am I today?\nWhat’s essential for me right now?`,
            title: `Me today`,
          },
          step2: {
            description: `Take a moment to reflect on your background and the history that has shaped you. Talk about the elements of your childhood or your family that come to mind, in all simplicity.`,
            title: `My roots`,
            questions: `How did I experience my childhood and my family growing up?\nAre there any stories or memories from that time that I still remember today?`,
          },
          step3: {
            description: `As we go through life, certain events leave a deep imprint on us. Take a moment to reflect on these key moments in your life, whether happy or difficult.`,
            questions: `What moments have had a profound impact on my career?\nHow do these experiences still influence the person I am today?\nIf I had to choose a key image of memory from my life, which would it be?`,
            title: `Footprints of my journey`,
          },
          step4: {
            description: `Our lives are shaped by the people we meet. Think about the people who have meant something to you and the mark they have left on you.`,
            title: `My journey companions`,
            questions: `Who are the people who have played a key role in my career?\nWhat did they awaken in me, positively or painfully?`,
          },
          step5: {
            description: `Describe in broad strokes what your days are like. Whether it’s work, school or other activities, this section will give you an overview of your daily routine.`,
            title: `My days in a nutshell`,
            questions: `What activities take up most of my day now (work, studies, commitments)?\nWhat do I like about what I do every day, and what weighs me down more?\nIs there anything else I’d like to do, or a field that particularly appeals to me?\nDoes my current activity reflect who I am?`,
          },
          step6: {
            description: `Talk about what nourishes you personally and helps you refocus on yourself. Here we’re interested in what you do for yourself, just for fun or to feel in harmony.`,
            title: `What resources me`,
            questions: `What moments have had a profound impact on my career?\nHow do these experiences still influence the person I am today?\nIf I had to choose a key image of memory from my life, which would it be?`,
          },
          step7: {
            description: `Look to the horizon and let your hopes for the future emerge. This section invites you to envision the future with your dreams and intentions.`,
            title: `Dreams and projects for tomorrow`,
            questions: `What direction do I want my life to take?\nWhat am I trying to build or achieve for the future?\nWhat would a life in harmony with who I am look like?`,
          },
          step8: {
            description: `Finally, let’s take a look at your inner world, and whats inside you in silence. This is an opportunity to evoke your deepest questions.`,
            title: `Whats inside me`,
            questions: `Do I feel free inside? From what, and for what?\nWhat does it mean to me to ‘give meaning to my life’?\nWhat do I fear in silence?`,
          },
        },
        saveAndContinue: `Save and continue later`,
      },
      questionnaireComplete: {
        title: `You've completed the questionnaire`,
        description: `Thank you for sharing, your insights help HARVN understand your past, present and direction for the future.\n\nYou can now either **edit your previous answers** or **submit your answers**. Once submitted, your answers will be saved to your profile and can be viewed anytime, they won't be editable.\n\nThis reflection forms the foundations of your self-discovery, HARVN is here to help guide and support you on every step of the journey.`,
        saveAndContinue: `Save and continue later`,
        previousQuestion: `Previous question`,
        submitQuestionnaire: `Submit questionnaire`,
        submitting: `Submitting...`,
        successToast: `Your questionnaire have been saved. You can view it in your profile.`,
      },
      questionnaireIncomplete: {
        title: `You've skipped a few questions.`,
        description: `Some responses are missing, but that's okay. You can choose to go back now to either **add to your unanswered questions** or **submit your answers** as they are.\n\nOnce submitted, your answers will be saved to your profile and can be viewed anytime, they won't be editable.\n\nThis reflection forms the foundations of your self-discovery, HARVN is here to help guide and support you on every step of the journey.`,
        saveAndContinue: `Save and continue later`,
        previousQuestion: `Previous question`,
        submitQuestionnaire: `Submit questionnaire`,
        submitting: `Submitting...`,
        successToast: `Your questionnaire have been saved. You can view it in your profile.`,
      },
    },
    pricing: {
      bestValue: `Best Value`,
      freemium: {
        buttonText: `Start free trial`,
        description: `No credit card required. Start your free trial on the web, then upgrade anytime in the mobile app to keep your HARVN experience going without interruption.`,
        title: `30 day Free Trial`,
        pricingFormat: `0CHF/30 days`,
      },
      monthly: {
        buttonText: `Purchase monthly plan`,
        description: `Enjoy HARVN on a monthly basis. Your plan renews each month, with the flexibility to cancel or upgrade to annual whenever you choose.`,
        title: `Monthly Plan`,
      },
      sectionTitle: `Plans`,
      title: `The support you need, on your terms.`,
      yearly: {
        buttonText: `Purchase yearly plan`,
        description: `Choose the yearly plan and get 2 months free. You can cancel or downgrade anytime, changes will take effect at the end of the yearly plan.`,
        title: `Yearly Plan`,
      },
      vatDisclaimer: `*Prices for Apple and Google include local VAT`,
      stores: {
        guidance: `All payments are managed through our mobile app. Choose your platform below to manage your subscriptions and invoices.`,
        sameEmail: `On the web, sign in with the same email address you use in the mobile app.`,
        android: {
          title: `Android Store`,
          description: `Get HARVN on Google Play to subscribe and manage your plan.`,
          badgeAlt: `Get it on Google Play`,
        },
        iphone: {
          title: `iPhone Store`,
          description: `Get HARVN on the App Store to subscribe and manage your plan.`,
          badgeAlt: `Download on the App Store`,
        },
      },
      confirmationModal: {
        cancel: `Cancel`,
        confirm: `Confirm changes`,
        freemiumToPaid: {
          title: `Upgrading from 30 day free trial!`,
          description1: `Your free trial will officially end if you upgrade to {{plan}}. Any remaining free trial days will be lost. Your card will be charged immediately and your billing cycle will start today.`,
          description2: `You will have uninterrupted access to HARVN — the adventure continues!`,
        },
        yearlyToMonthly: {
          title: `You've chosen to switch to the HARVN monthly plan.`,
          description1: `You are currently on the yearly plan, which will remain active until it ends on {{endDate}}.`,
          description2: `After that, your subscription will automatically renew on a Monthly Plan. You won't be charged for the Monthly plan until your Yearly plan expires.`,
        },
        monthlyToYearly: {
          title: `You've chosen to switch to the HARVN Yearly Plan.`,
          description1: `You are currently on the monthly plan, which will remain active until it ends on {{endDate}}.`,
          description2: `After that, your subscription will automatically renew to a Yearly Plan. You won't be charged for the Yearly plan until your Monthly plan expires.`,
        },
      },
    },
    profile: {
      deleteAccount: {
        button: `Delete account`,
        confirmDialog: {
          cancel: `No, keep my account`,
          confirm: `Delete Account`,
          description: `To continue deleting your account please be aware of the following information.`,
          description2: `If you change your mind, you can reactivate your account anytime within the next 90 days, during this time your preferences will be saved.`,
          description3: `If you require your account to be deleted immediately, email us at support@harvn.com and we'll be in touch.`,
          title: `Are you sure you want to delete your account?`,
        },
        description: `This will permanently delete your personal account. Please note that this action is irreversible, please proceed with caution.`,
        title: `Delete account`,
        postDeletion: {
          title: `Account Deletion Initiated`,
          description: `Your account has been scheduled for deletion. Here's what happens next:`,
          whatHappensNext: `What happens next?`,
          step1: `Your account is now in a **soft-deleted state** - you cannot log in or access any features.`,
          step2: `Your data and preferences will be **securely stored for 90 days** starting today.`,
          step3: `On day 90, your account and **all associated data will be permanently deleted** from our systems.`,
          step4: `You will receive an **email confirmation** when the permanent deletion is complete.`,
          reactivationTitle: `Changed your mind?`,
          reactivationInfo: `You can reactivate your account **anytime within the next 90 days** by emailing **support@harvn.com** with your account details. All your preferences will be restored.`,
          contactInfo: `For questions or immediate deletion, contact **support@harvn.com**`,
          continue: `I understand, delete my account`,
        },
      },
      footer: {
        privacyPolicy: `Privacy Policy`,
        termsConditions: `Terms & Conditions`,
      },
      profileDetails: {
        description: `View and update your customer details here.`,
        emailAddress: `Email address`,
        title: `Profile details`,
        firstName: `First name`,
        lastName: `Last name`,
        saveButton: `Save changes`,
        saveSuccess: `Profile updated successfully`,
        saveError: `Failed to update profile. Please try again.`,
        nameRequired: `At least one name field must be filled`,
      },
      questionnaire: {
        badge: `Submitted {{date}}`,
        description: `You can reflect when you first started using HARVN and acknowledge your changes and milestones.`,
        title: `Questionnaire`,
        viewQuestionnaire: `View Questionnaire`,
        banner: {
          title: `Do you have time to add to your questionnaire?`,
          description: `I'm missing some context, feel free to include any additional details.`,
          finishButton: `Finish questionnaire`,
        },
      },
      questionnaireModal: {
        answerSkipped: `Question was skipped`,
        close: `Close`,
        description: `When you joined HARVN, you were asked 8 reflective questions. Below are all the questions and your answers.`,
        error: `Failed to load questionnaire data`,
        loading: `Loading questionnaire...`,
        noQuestionnaire: `No questionnaire data available`,
        noQuestions: `No questions found`,
        print: `Print`,
        purposeAlert: `The purpose of this document is to provide reflection, to have a starting milestone on your journey with HARVN`,
        question: `Question`,
        retry: `Try again`,
        title: `Questionnaire`,
      },
      subscriptionBanner: {
        freemium: {
          description: `You currently have limited access. Upgrade to a subscription plan.`,
          title: `You are using the free trial version.`,
          viewPlans: `View subscription plans`,
        },
        monthly: {
          title: `You are using a monthly subscription`,
          description: `You currently have full access`,
          viewPlans: `View subscription plans`,
        },
        yearly: {
          title: `You're using a yearly subscription`,
          description: `You currently have full access`,
        },
      },
      privacyModal: {
        title: `Privacy Policy`,
      },
      termsModal: {
        title: `Terms of Service`,
      },
      title: `My Profile`,
    },
    settings: {
      appearance: {
        dark: `Dark`,
        description: `Select your preferred mode`,
        light: `Light`,
        system: `Device default`,
        title: `Appearance`,
      },
      billing: {
        description: `Update your billing method`,
        title: `Billing`,
        updateButton: `Update`,
      },
      language: {
        description: `Update your referred language here.`,
        label: `Language`,
        title: `Select language`,
      },
      subscription: {
        cancelPlan: `Cancel plan`,
        charge: `{{amount}}{{currency}}`,
        currentPlan: `Current plan`,
        description: `View and update your subscription plan here.`,
        freemiumBanner: {
          title: `You're on the free trial`,
          description: `Explore all features during your free trial period.`,
          viewPlans: `View plans`,
        },
        freemiumSubscription: `Free 30 day trial, expires {{date}}.`,
        fullAccess: {
          included: `Included in Full Access`,
          title: `Full Access`,
        },
        monthlySubscription: `Monthly subscription`,
        monthlyPlanInfo: `All plan changes are activated on the following billing cycle.`,
        nextBilling: `Next billing cycle {{date}}`,
        nextBillingDate: `Next billing cycle {{date}}`,
        paymentMethod: `Paid with {{method}} ending in {{last4}}`,
        selectPlan: `Select plan`,
        title: `Subscription details`,
        upgradePlan: `Upgrade plan`,
        upgradeToYearly: `Upgrade plan`,
        viewPlans: `View plans`,
        yearlySubscription: `Yearly subscription`,
        yearlyPlanInfo: `You can change to a Monthly plan once your Yearly access expires. It will remain active until {{endDate}}.`,
        yourPlan: `Your plan`,
        cancelPlanDisabled: `No active subscription to cancel`,
        noData: `No subscription information available`,
        noDataDescription: `We couldn't load your subscription information. Please try again or contact support if the problem persists.`,
        cancellation: {
          title: `Subscription Cancelled`,
          description: `Your subscription has been successfully cancelled.`,
          accessUntil: `You will have access until`,
          reactivationWindow: `Reactivation available`,
          reactivationDescription: `You can re-activate your plan within 90 days without data loss`,
          cancelledOn: `Cancelled on`,
          continue: `Continue`,
        },
        cancelled: {
          title: `Subscription Cancelled`,
          description: `Your subscription has been cancelled but you still have access.`,
          accessUntil: `Access until {{date}}`,
          daysRemaining: `{{days}} days remaining`,
          reactivateDescription: `You can reactivate your subscription at any time to continue with uninterrupted access.`,
          reactivateButton: `Reactivate Subscription`,
        },
        cancellingAtPeriodEnd: {
          title: `Subscription Ending`,
          description: `Your subscription will end at the current billing period.`,
          accessUntil: `Access until {{date}}`,
          daysRemaining: `{{days}} days remaining`,
          canSubscribeNote: `You can subscribe to a new plan now, and it will start after this one expires.`,
          reactivateDescription: `You can reactivate your subscription before it ends to continue with uninterrupted access.`,
          reactivateButton: `Reactivate Subscription`,
        },
        pastDue: {
          title: `Payment Past Due`,
          description: `Your subscription payment is overdue. Please update your payment method.`,
          accessUntil: `Limited access until {{date}}`,
          updatePaymentDescription: `Update your payment method to restore full access to your subscription.`,
          updatePaymentButton: `Update Payment Method`,
        },
        statusDisplayNames: {
          freemium: `30 day Free Trial`,
          monthly: `Full Access Monthly`,
          yearly: `Full Access Yearly`,
          cancelled: `Full Access (Cancelled)`,
          cancellingAtPeriodEnd: `Full Access (Ending {{date}})`,
          pastDue: `Full Access (Payment Due)`,
          fullAccess: `Full Access`,
        },
      },
      title: `Settings`,
    },
  },
};
